import {
  MenuContent,
  MenuItem,
  MenuPositioner,
  MenuRoot,
  Portal,
  type MenuContentProps,
  type MenuItemProps,
  type MenuRootProps,
} from '@chakra-ui/react'
import { forwardRef } from 'react'

type OpenChangeDetails = {
  open: boolean
}

interface DropdownMenuProperties extends Omit<MenuRootProps, 'onOpenChange'> {
  onOpenChange?: (open: boolean) => void
}

type DropdownMenuContentProperties = Omit<MenuContentProps, 'className'> & {
  className?: string
}

type DropdownMenuItemProperties = Omit<MenuItemProps, 'className'> & {
  className?: string
}

export const DropdownMenu = ({ onOpenChange, ...properties }: DropdownMenuProperties) => {
  /* --- Section 6: Render --- */
  return (
    <MenuRoot
      {...properties}
      onOpenChange={(details: OpenChangeDetails) => {
        onOpenChange?.(details.open)
      }}
    />
  )
}
export { MenuTrigger as DropdownMenuTrigger } from '@chakra-ui/react'

export const DropdownMenuContent = forwardRef<HTMLDivElement, DropdownMenuContentProperties>(
  ({ className, children, ...properties }, reference) => {
    /* --- Section 3: Derived Values --- */
    const contentClassName = typeof className === 'string' ? `ui-dropdown-content ${className}` : 'ui-dropdown-content'

    /* --- Section 6: Render --- */
    return (
      <Portal>
        <MenuPositioner>
          <MenuContent
            {...properties}
            bg="var(--theme-card-background)"
            border="[1px solid var(--theme-accent-border)]"
            borderRadius="2xl"
            boxShadow="var(--shadow-lg)"
            className={contentClassName}
            minW="[240px]"
            p="[12px]"
            ref={reference}
          >
            {children}
          </MenuContent>
        </MenuPositioner>
      </Portal>
    )
  }
)

export const DropdownMenuItem = forwardRef<HTMLDivElement, DropdownMenuItemProperties>(
  ({ className, ...properties }, reference) => {
    /* --- Section 3: Derived Values --- */
    const itemClassName = typeof className === 'string' ? `ui-dropdown-item ${className}` : 'ui-dropdown-item'

    /* --- Section 6: Render --- */
    return (
      <MenuItem
        {...properties}
        borderRadius="[var(--radius-sm)]"
        className={itemClassName}
        color="var(--theme-text-color)"
        cursor="[pointer]"
        minH="[46px]"
        px="[18px]"
        py="[12px]"
        ref={reference}
        _focus={{
          bg: 'var(--theme-accent-background)',
          color: 'var(--theme-text-color)',
        }}
        _hover={{
          bg: 'var(--theme-accent-background)',
        }}
      />
    )
  }
)

DropdownMenuContent.displayName = 'DropdownMenuContent'
DropdownMenuItem.displayName = 'DropdownMenuItem'
